'use client';

/**
 *@providesModule SpotlightMaskedView
 */
import {useCallback, useEffect, useMemo} from 'react';
import {useMaskCoordinates} from './useMaskCoordinates';
import {HighlightArrow} from '@sdk-mobile';
import type {SpotlightMaskInsets, SpotlightArrowLayout, SpotlightArrowOffset} from './types';
import {useArrowPosition} from '@/sdk-mobile-web/SpotlightMaskedView/useArrowPosition';
import {createPortal} from 'react-dom';

type Props = {
  maskRef?: HTMLElement;
  maskRefs?: HTMLElement[];
  refCallback: () => any;
  cornerRadius?: number;
  onHighlightedPress?: () => any;
  onSpotlightMaskPress?: (event: any) => any;
  insets?: SpotlightMaskInsets;
  arrowDirection: 'top' | 'bottom' | 'left' | 'right';
  arrowOffset?: SpotlightArrowOffset;
  arrowLayout?: SpotlightArrowLayout;
  acceptTouches?: boolean;
  showSpotlight?: boolean;
  hideArrow?: boolean;
  shouldHide?: boolean;
  showAnimation?: boolean;
  maskAccessibilityLabel?: string;
  maskTestID?: string;
  spotlightKey?: string;
  animationDuration?: number;
  animationInterval?: number;
  animationOpacity?: number;
};

export default function SpotlightMaskedView(props: Props) {
  const {
    acceptTouches,
    onSpotlightMaskPress,
    // showSpotlight = true,
    hideArrow = false,
    maskRef,
    maskRefs = [],
    insets,
    spotlightKey = 'spotlight',
    arrowDirection,
    // arrowOffset = {
    //   x: 0,
    //   y: 0,
    // },
    arrowLayout,
  } = props;

  const nodes = useMemo(() => (maskRef ? [maskRef, ...maskRefs] : maskRefs), [maskRef, maskRefs]);
  const coordinates = useMaskCoordinates(nodes, insets);

  const arrowPosition = useArrowPosition(arrowLayout, maskRef);
  const showArrow = !hideArrow && arrowPosition;

  const handleNodeClick = useCallback(
    (event: MouseEvent) => {
      if (acceptTouches) {
        onSpotlightMaskPress?.(event);
      } else {
        event.stopPropagation();
      }
    },
    [acceptTouches, onSpotlightMaskPress]
  );

  useEffect(() => {
    if (maskRef) {
      maskRef.addEventListener('click', handleNodeClick);
    }
    if (maskRefs.length > 0) {
      maskRefs.forEach((ref) => {
        if (ref) {
          ref.addEventListener('click', handleNodeClick);
        }
      });
    }
    return () => {
      if (maskRef) {
        maskRef.removeEventListener('click', handleNodeClick);
      }
      if (maskRefs.length > 0) {
        maskRefs.forEach((ref) => {
          if (ref) {
            ref.removeEventListener('click', handleNodeClick);
          }
        });
      }
    };
  }, [maskRef, maskRefs, handleNodeClick]);

  return createPortal(
    <div
      className='fixed inset-0 w-full h-ful z-20'
      key={`${spotlightKey}`}
      style={{pointerEvents: 'none'}}
    >
      {coordinates.map((coord) => (
        <div
          key={coord.key}
          style={{
            position: 'absolute',
            top: coord.top,
            left: coord.left,
            width: coord.width,
            height: coord.height,
            background: 'rgba(0,0,0,.6)',
            pointerEvents: 'all',
          }}
        ></div>
      ))}
      {showArrow ? (
        <HighlightArrow
          direction={arrowDirection}
          shouldBounce={true}
          refLayout={arrowPosition}
          offset={{x: 0, y: 0}}
          imageURL={'ic_ftue_filled_arrow_skz.png'}
        />
      ) : null}
    </div>,
    document.getElementById('spotlight-root') as Element
  );
}
